import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import api from '../services/api';
import { ArrowLeft, Pencil, FileText, Gauge, ChevronRight, ClipboardList, ReceiptText, CalendarClock } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonCard } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import EditServiceModal from '../components/services/EditServiceModal';
import ServiceInvoiceHistory from '../components/services/ServiceInvoiceHistory';
import { useAllServices } from '../hooks/useServices';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const categoryBadge = {
    electricity: 'bg-category-electricity/10 text-category-electricity',
    natural_gas: 'bg-category-gas/10 text-category-gas',
    water: 'bg-category-water/10 text-category-water',
    phone: 'bg-category-phone/10 text-category-phone',
    internet: 'bg-category-internet/10 text-category-internet',
    other: 'bg-surface-overlay text-text-muted',
};

const ServiceDetail = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { data: allServices = [], isLoading: loading, error } = useAllServices();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [overviewData, setOverviewData] = useState([]);

    useEffect(() => {
        if (!uid) return;
        api
            .getServiceOverview(uid)
            .then((data) => {
                setOverviewData(data);
                console.log('Service overview:', data);
            })
            .catch(console.error);
    }, [uid]);

    const service = allServices.find((s) => s.uid === uid);

    useEffect(() => {
        if (!uid) return;
        api.getServiceOverview(uid).then((data) => console.log('Service overview:', data));
    }, [uid]);

    // Build typeLabels inside component so translations apply
    const typeLabels = {
        electricity: t(KEYS.TYPE_ELECTRICITY),
        natural_gas: t(KEYS.TYPE_NATURAL_GAS),
        water: t(KEYS.TYPE_WATER),
        phone: t(KEYS.TYPE_PHONE),
        internet: t(KEYS.TYPE_INTERNET),
        other: t(KEYS.TYPE_OTHER),
    };

    if (loading) {
        return (
            <div className="space-y-5">
                <div className="flex items-center gap-3">
                    <button className="p-1.5 rounded-md text-text-muted">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="h-8 w-48 bg-surface-overlay rounded animate-pulse" />
                </div>
                <SkeletonCard />
            </div>
        );
    }

    if (error || !service) {
        return (
            <div className="space-y-5">
                <ErrorState message={error?.message || t(KEYS.SERVICES_NOT_FOUND)} />
                <div className="flex justify-center">
                    <Button variant="ghost" onClick={() => navigate('/services')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {t(KEYS.SERVICES_BACK)}
                    </Button>
                </div>
            </div>
        );
    }

    const typeLabel = typeLabels[service.type] || service.type;
    const badgeClass = categoryBadge[service.type] || categoryBadge.other;

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/services')}
                        className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface-overlay transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-text-primary">
                            {service.name || typeLabel}
                        </h1>
                        {service.name && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider mt-1 ${badgeClass}`}>
                                {typeLabel}
                            </span>
                        )}
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setIsEditModalOpen(true)}
                    className="gap-1.5"
                >
                    <Pencil className="w-3.5 h-3.5" />
                    {t(KEYS.SERVICE_DETAIL_EDIT)}
                </Button>
            </div>

            {/* Details */}
            <Card>
                <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
                    {t(KEYS.SERVICE_DETAIL_DETAILS)}
                </h2>
                <div className="space-y-3">
                    {service.name && (
                        <div>
                            <span className="label-base">{t(KEYS.SERVICE_DETAIL_TYPE)}</span>
                            <p className="text-sm text-text-primary mt-1">{typeLabel}</p>
                        </div>
                    )}
                    {service.description && (
                        <div>
                            <span className="label-base">{t(KEYS.SERVICE_DETAIL_DESCRIPTION)}</span>
                            <p className="text-sm text-text-primary mt-1">{service.description}</p>
                        </div>
                    )}
                    <div>
                        <span className="label-base">{t(KEYS.SERVICE_DETAIL_SUPPLIER)}</span>
                        <div className="mt-1">
                            <Link
                                to={`/suppliers/${service.supplierUid}`}
                                className="text-sm text-accent hover:underline"
                            >
                                {service.supplierName}
                            </Link>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Invoice History Chart */}
            <Card>
                <h2 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">
                    {t(KEYS.SERVICE_DETAIL_INVOICE_HISTORY)}
                </h2>
                <ServiceInvoiceHistory data={overviewData} />
            </Card>

            {/* Sub-page navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                    to={`/services/${uid}/readings`}
                    className="group flex items-center justify-between p-4 bg-surface-raised border border-surface-border rounded-lg hover:border-accent/40 hover:bg-surface-overlay transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-overlay rounded-md group-hover:bg-accent/10 transition-colors">
                            <Gauge className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.SERVICE_DETAIL_NAV_READINGS)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.SERVICE_DETAIL_NAV_READINGS_SUB)}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                </Link>

                <Link
                    to={`/services/${uid}/invoices`}
                    className="group flex items-center justify-between p-4 bg-surface-raised border border-surface-border rounded-lg hover:border-accent/40 hover:bg-surface-overlay transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-overlay rounded-md group-hover:bg-accent/10 transition-colors">
                            <FileText className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.SERVICE_DETAIL_NAV_INVOICES)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.SERVICE_DETAIL_NAV_INVOICES_SUB)}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                </Link>

                <Link
                    to={`/services/${uid}/reading-templates`}
                    className="group flex items-center justify-between p-4 bg-surface-raised border border-surface-border rounded-lg hover:border-accent/40 hover:bg-surface-overlay transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-overlay rounded-md group-hover:bg-accent/10 transition-colors">
                            <ClipboardList className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.SERVICE_DETAIL_NAV_READING_TEMPLATES)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.SERVICE_DETAIL_NAV_READING_TEMPLATES_SUB)}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                </Link>

                <Link
                    to={`/services/${uid}/invoice-templates`}
                    className="group flex items-center justify-between p-4 bg-surface-raised border border-surface-border rounded-lg hover:border-accent/40 hover:bg-surface-overlay transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-overlay rounded-md group-hover:bg-accent/10 transition-colors">
                            <ReceiptText className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.SERVICE_DETAIL_NAV_INVOICE_TEMPLATES)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.SERVICE_DETAIL_NAV_INVOICE_TEMPLATES_SUB)}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                </Link>

                <Link
                    to={`/services/${uid}/actions`}
                    className="group flex items-center justify-between p-4 bg-surface-raised border border-surface-border rounded-lg hover:border-accent/40 hover:bg-surface-overlay transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-surface-overlay rounded-md group-hover:bg-accent/10 transition-colors">
                            <CalendarClock className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-primary">{t(KEYS.SERVICE_DETAIL_NAV_ACTIONS)}</p>
                            <p className="text-xs text-text-muted">{t(KEYS.SERVICE_DETAIL_NAV_ACTIONS_SUB)}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" />
                </Link>
            </div>

            <EditServiceModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                service={service}
                supplierUid={service.supplierUid}
            />
        </div>
    );
};

export default ServiceDetail;
